import { createAsyncAction, errorResult, successResult } from 'pullstate';
import React from 'react';
import Async from 'react-select/async';
import styled, { ThemeContext } from 'styled-components';
import { v4 } from 'uuid';

import { IPage } from 'api';
import { IGroup } from 'api/GroupsAPI';
import SearchAPI from 'api/SearchAPI';
import Input from 'components/Input';
import getStyles from 'containers/SearchInput/getStyles';

const fetchSearchResults = createAsyncAction<{ term: string }, IPage<IGroup>>(async ({ term }) => {
  try {
    const json = await SearchAPI.searchGroups(term);
    if (!json.success) throw new Error(json.error.message);

    return successResult(json.response);
  } catch (err) {
    return errorResult([], err.message);
  }
});

const loadOptions: React.ComponentProps<typeof Async>['loadOptions'] = async (term) => {
  if (term.length < 3) return [];

  const json = await fetchSearchResults.run({ term });
  const items = json?.payload?.items ?? [];
  return items.map((item) => ({
    value: item.id,
    item,
    label: item.title,
  }));
};

type AsyncProps = Omit<React.ComponentProps<typeof Async>, 'styles'>;

const SCWrapper = styled.div<{ noMargin: boolean }>`
  margin: ${(props) => (props.noMargin ? 0 : '0 0 40px')};
  max-width: 610px;
`;

const SCLabel = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 10px;
`;

interface IProps {
  id?: string;
  label?: string;
  noMargin?: boolean;
}

const GroupSearchInput: React.FC<AsyncProps & IProps> = ({
  id = v4(),
  noMargin = false,
  label,
  ...props
}) => {
  const theme = React.useContext(ThemeContext);
  return (
    <SCWrapper noMargin={noMargin}>
      {label && <SCLabel htmlFor={id}>{label}</SCLabel>}
      <Async id={id} styles={getStyles(theme.dark)} loadOptions={loadOptions} {...props} />
    </SCWrapper>
  );
};

interface ILocalProps {
  id?: string;
  label?: string;
  noMargin?: boolean;
  value: string;
  onChange: (e: string) => void;
  placeholder?: string;
}

export const LocalGroupSearchInput: React.FC<ILocalProps> = ({
  id = v4(),
  noMargin = false,
  label,
  value,
  onChange,
  placeholder,
  ...props
}) => {
  const theme = React.useContext(ThemeContext);
  return (
    <SCWrapper noMargin={noMargin}>
      {label && <SCLabel htmlFor={id}>{label}</SCLabel>}
      <Input id={id} type="text" styles={getStyles(theme.dark)} value={value} onChange={onChange} placeholder={placeholder} {...props} />
    </SCWrapper>
  );
};

export default GroupSearchInput;
